import { useEffect, useMemo, useState } from 'react';
import {
  FastArrowLeft,
  FastArrowRight,
  NavArrowLeft,
  NavArrowRight,
  Sparks,
} from 'iconoir-react';
import {
  ChevronDown,
  Copy,
  Download,
  FileText,
  Layers3,
  Loader2,
  MessageSquare,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Trash,
  Upload,
  Wrench,
} from 'lucide-react';
import ConfirmationModal from 'components/blocks/confirmation-modal/confirmation-modal';
import { ResourceCard, ResourceCardLogo, ResourceCardMenu } from 'components/blocks/resource-card';
import { Card } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Button } from 'components/ui/button';
import { Badge } from 'components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { useToast } from 'hooks/use-toast';
import { useIsOwner } from 'hooks/use-is-owner';
import { useConfig } from 'hooks/use-config';
import { AddFolderToAgentDialog } from 'pages/knowledge-base/components/add-folder-to-agent-dialog';
import { cn } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import { CreateSkillModal } from './create-skill-modal';
import { ImportSkillModal } from './import-skill-modal';
import { SkillDetailsModal } from './skill-details-modal';
import { SkillVisibilityBadge } from './skill-visibility';
import {
  useDeleteSkill,
  useDownloadSkill,
  useDuplicateSkill,
  useGetSkills,
  useInstallSkill,
  useUninstallSkill,
} from './use-skills';
import { SkillListItem } from './skills.types';
import { getApiErrorDetail } from 'utils/api-error';
import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import { configureCardGridClassName, configureCardSizeClassName } from 'constant/configure-layout';

type SkillsTab = 'all' | 'installed';

const SKILLS_PAGE_SIZE = 20;

const skillsPaginationButtonClassName =
  'h-8 w-8 rounded-full border border-grey-100 bg-white p-0 text-grey-900 shadow-none hover:bg-white hover:text-grey-900 disabled:border-grey-100 disabled:bg-white disabled:text-grey-250 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-card dark:disabled:border-border dark:disabled:bg-card dark:disabled:text-muted-foreground';

const createSkillButtonClassName =
  'bg-black text-indigo-10 hover:bg-grey-900 hover:text-indigo-10 dark:bg-grey-800 dark:text-white dark:hover:bg-grey-900 dark:hover:text-white';

function SkillsListSkeleton() {
  return (
    <div className={configureCardGridClassName}>
      {Array.from({ length: 8 }).map((_, index) => (
        <Card
          key={index}
          className="h-[240px] min-h-[240px] overflow-hidden rounded-lg border border-grey-50 bg-white shadow-none dark:border-border dark:bg-card"
        >
          <div className="flex h-full flex-col gap-3 p-4">
            <div className="flex min-w-0 items-center justify-between gap-2">
              <div className="h-10 w-10 shrink-0 animate-pulse rounded-xl bg-muted" />
              <div className="flex shrink-0 items-center gap-1">
                <div className="h-7 w-16 shrink-0 animate-pulse rounded-md bg-muted" />
                <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-muted" />
              </div>
            </div>
            <div className="h-4 w-32 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
            <div className="mt-auto flex items-center justify-between gap-2 pt-4">
              <div className="h-6 w-10 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-5 w-24 animate-pulse rounded-md bg-muted" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

const getSkillDescription = (
  skill: SkillListItem,
  translate: (key: string) => string
) => {
  const description = skill.description?.trim();
  if (description) {
    return description;
  }

  const instructions = skill.instructions?.replace(/#+/g, '').replace(/\s+/g, ' ').trim();
  return (
    instructions ||
    translate('SKILLS_NO_DESCRIPTION') ||
    'No description available for this skill yet.'
  );
};

function SkillCard({
  skill,
  onOpen,
  onInstallToggle,
  isPending,
  onDeleteClick,
  onDuplicateClick,
  onDownloadClick,
  isDuplicating,
  isDownloading,
}: {
  skill: SkillListItem;
  onOpen: (skillId: string) => void;
  onInstallToggle: (skill: SkillListItem) => void;
  isPending: boolean;
  onDeleteClick: (skill: SkillListItem) => void;
  onDuplicateClick: (skill: SkillListItem) => void;
  onDownloadClick: (skill: SkillListItem) => void;
  isDuplicating: boolean;
  isDownloading: boolean;
}) {
  const { t } = useTranslation();
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const [isAddToAgentOpen, setIsAddToAgentOpen] = useState(false);
  const canManage = useIsOwner(skill) && !skill.isSystem;
  const isInstalled = skill.installed;
  const isLockedInstalled = skill.forceEnabled;
  const description = getSkillDescription(skill, t);
  const toolsCount = skill.requiredToolIds.length;
  const toolsLabel =
    t('N_TOOLS_CONNECTED', {
      count: toolsCount,
      defaultValue: `${toolsCount} tools connected`,
    }) || `${toolsCount} tools connected`;

  return (
    <>
    <ResourceCard
      title={skill.name || t('SKILLS_UNTITLED') || 'Untitled skill'}
      description={description}
      className={configureCardSizeClassName}
      icon={
        <ResourceCardLogo src={skill.iconUrl} name={skill.name || t('SKILLS_UNTITLED') || 'Untitled skill'}>
          <Layers3 className="h-5 w-5" />
        </ResourceCardLogo>
      }
      badge={
        skill.forceEnabled ? (
          <Badge
            variant="secondary"
            className="h-5 w-fit shrink-0 rounded-full border-0 bg-foreground px-2 py-0 text-[10px] font-medium text-background hover:bg-foreground hover:text-background"
          >
            {t('SKILLS_CORE') || 'Core'}
          </Badge>
        ) : null
      }
      actions={
        <>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={cn(
              'h-6 shrink-0 rounded-full px-2.5 text-[11px] font-medium shadow-none transition-colors',
              isLockedInstalled
                ? 'bg-[#ecfdf3] text-[#079455] hover:bg-[#ecfdf3] hover:text-[#079455] dark:bg-emerald-950/40 dark:text-emerald-300 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300'
                : isInstalled
                  ? 'border border-[#e7e7e7] bg-[#f6f6f6] text-[#3d3d3d] hover:bg-[#ebebeb] hover:text-[#1a1a1a] dark:border-true-white/20 dark:bg-true-white/10 dark:text-true-white dark:hover:bg-true-white/20 dark:hover:text-true-white'
                  : 'bg-black text-white hover:bg-grey-900 hover:text-white dark:bg-true-white dark:text-black-base dark:hover:bg-grey-100 dark:hover:text-black-base'
            )}
            disabled={isPending || isLockedInstalled}
            onClick={(event) => {
              event.stopPropagation();
              onInstallToggle(skill);
            }}
          >
            {isLockedInstalled ? (
              t('SKILLS_INSTALLED') || 'Installed'
            ) : isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : isInstalled ? (
              t('SKILLS_UNINSTALL') || 'Uninstall'
            ) : (
              t('SKILLS_INSTALL') || 'Install'
            )}
          </Button>
          <ResourceCardMenu
            items={[
              canManage && {
                label: t('EDIT') || 'Edit',
                icon: <Pencil />,
                onClick: () => onOpen(skill.id),
              },
              {
                label: t('DUPLICATE') || 'Duplicate',
                icon: <Copy />,
                onClick: () => onDuplicateClick(skill),
                disabled: isDuplicating,
              },
              {
                label: t('DOWNLOAD') || 'Download',
                icon: <Download />,
                onClick: () => onDownloadClick(skill),
                disabled: isDownloading,
              },
              {
                label: t('ADD_TO_AGENT') || 'Add to agent',
                icon: <Sparks strokeWidth={1.5} />,
                onClick: () => setIsAddToAgentOpen(true),
              },
              canManage && {
                label: t('DELETE') || 'Delete',
                icon: <Trash />,
                onClick: () => onDeleteClick(skill),
                destructive: true,
              },
            ]}
          />
        </>
      }
      onSelect={() => onOpen(skill.id)}
      footer={
        <div className="flex min-h-7 items-center justify-between gap-2">
          {toolsCount > 0 ? (
            <span className="flex min-w-0 flex-1 items-center gap-1.5" title={toolsLabel}>
              <span
                className={cn(
                  'flex size-6 shrink-0 items-center justify-center rounded-full border',
                  'border-amber-200 bg-amber-50 text-amber-600 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-400'
                )}
              >
                <Wrench className="size-3.5" strokeWidth={1.75} />
              </span>
              <span className="min-w-0 truncate text-xs font-normal text-grey-500 dark:text-muted-foreground">
                {toolsLabel}
              </span>
            </span>
          ) : null}
          <div className="ml-auto flex min-w-0 shrink-0 items-center gap-1.5">
            <SkillVisibilityBadge value={skill.visibility} className="shrink-0" />
          </div>
        </div>
      }
    />

    <AddFolderToAgentDialog
      open={isAddToAgentOpen}
      onOpenChange={setIsAddToAgentOpen}
      projectKey={projectKey}
      resourceId={skill.id}
      queryParam="addSkill"
      description={
        t('SELECT_AGENT_TO_CONNECT_SKILL') || 'Select an agent to connect this skill.'
      }
    />
    </>
  );
}

export default function SkillsPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<SkillsTab>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [isCreateSkillOpen, setIsCreateSkillOpen] = useState(false);
  const [createSkillDraftMode, setCreateSkillDraftMode] = useState(false);
  const [isImportSkillOpen, setIsImportSkillOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<SkillListItem | null>(null);
  const [pendingSkillAction, setPendingSkillAction] = useState<{
    skillId: string;
    action: 'install' | 'uninstall';
  } | null>(null);
  const [duplicatingSkillId, setDuplicatingSkillId] = useState<string | null>(null);
  const [downloadingSkillId, setDownloadingSkillId] = useState<string | null>(null);
  const { toast } = useToast();
  const { mutateAsync: installSkill } = useInstallSkill();
  const { mutateAsync: uninstallSkill } = useUninstallSkill();
  const { mutateAsync: deleteSkill, isPending: isDeletingSkill } = useDeleteSkill();
  const { mutateAsync: duplicateSkill } = useDuplicateSkill();
  const { mutateAsync: downloadSkill } = useDownloadSkill();

  const trimmedSearch = search.trim();
  const skillsQueryFilters = useMemo(
    () => ({
      ...(trimmedSearch ? { search_query: trimmedSearch } : {}),
    }),
    [trimmedSearch]
  );

  const { data: skillsTotalData, isLoading: isTotalCountLoading } = useGetSkills(
    {
      page: 1,
      pageSize: 100,
      ...skillsQueryFilters,
    },
    { scope: 'total-count' }
  );

  const { data, isLoading, isError, refetch } = useGetSkills(
    {
      page,
      pageSize: SKILLS_PAGE_SIZE,
      ...skillsQueryFilters,
    },
    { scope: 'list' }
  );

  const skills = useMemo(() => data?.data ?? [], [data?.data]);

  const coreSkillsCount = useMemo(() => {
    const totalList = skillsTotalData?.data ?? [];
    if (totalList.length > 0) {
      return totalList.filter((skill) => skill.forceEnabled).length;
    }
    return skills.filter((skill) => skill.forceEnabled).length;
  }, [skillsTotalData?.data, skills]);

  const rawTotalCount = skillsTotalData?.totalCount ?? 0;
  const totalCount = Math.max(0, rawTotalCount - coreSkillsCount);
  const pageCount = Math.max(1, Math.ceil(totalCount / SKILLS_PAGE_SIZE));

  useEffect(() => {
    setPage(1);
  }, [trimmedSearch, activeTab]);

  useEffect(() => {
    if (!isLoading && totalCount > 0 && page > pageCount) {
      setPage(pageCount);
    }
  }, [page, pageCount, isLoading, totalCount]);

  const visibleSkills = useMemo(
    () => skills.filter((skill) => !skill.forceEnabled),
    [skills]
  );

  const installedSkills = useMemo(
    () => visibleSkills.filter((skill) => skill.installed),
    [visibleSkills]
  );

  const installedCount = useMemo(() => {
    const totalList = skillsTotalData?.data ?? [];
    if (totalList.length > 0) {
      return totalList.filter((skill) => !skill.forceEnabled && skill.installed).length;
    }
    return installedSkills.length;
  }, [skillsTotalData?.data, installedSkills.length]);

  const filteredSkills = useMemo(() => {
    if (activeTab === 'installed') {
      return installedSkills;
    }

    return visibleSkills;
  }, [activeTab, installedSkills, visibleSkills]);

  const tabs = useMemo(
    () =>
      [
        { label: t('SKILLS_TAB_ALL') || 'All', value: 'all' as const },
        { label: t('SKILLS_TAB_INSTALLED') || 'Installed', value: 'installed' as const },
      ] satisfies Array<{ label: string; value: SkillsTab }>,
    [t]
  );

  const totalVisible = activeTab === 'installed' ? installedCount : totalCount;
  const sectionTitle =
    activeTab === 'installed'
      ? t('SKILLS_SECTION_INSTALLED') || 'Installed skills'
      : t('SKILLS_SECTION_ALL') || 'All skills';
  const skillToDeleteName =
    skillToDelete?.name || t('SKILLS_THIS_SKILL') || 'this skill';
  const skillCountLabel =
    totalVisible === 1
      ? t('SKILLS_COUNT_ONE') || 'skill'
      : t('SKILLS_COUNT_MANY') || 'skills';
  const createSkillMenuItemClassName =
    'flex min-w-[280px] items-start gap-3 rounded-xl px-3 py-3 text-left focus:bg-muted/70 focus:text-foreground';
  const createSkillMenuIconClassName = 'mt-0.5 h-4 w-4 shrink-0 text-foreground';

  const handleInstallToggle = async (skill: SkillListItem) => {
    if (skill.forceEnabled) {
      return;
    }

    try {
      if (skill.installed) {
        setPendingSkillAction({ skillId: skill.id, action: 'uninstall' });
        await uninstallSkill(skill.id);
        toast({
          variant: 'success',
          title: t('SKILLS_TOAST_UNINSTALLED_TITLE') || 'Skill uninstalled',
          description:
            t('SKILLS_TOAST_UNINSTALLED_DESCRIPTION', { name: skill.name }) ||
            `${skill.name} has been removed from installed skills.`,
        });
        return;
      }

      setPendingSkillAction({ skillId: skill.id, action: 'install' });
      await installSkill(skill.id);
      toast({
        variant: 'success',
        title: t('SKILLS_TOAST_INSTALLED_TITLE') || 'Skill installed',
        description:
          t('SKILLS_TOAST_INSTALLED_DESCRIPTION', { name: skill.name }) ||
          `${skill.name} is now available as an installed skill.`,
      });
    } catch (error) {
      toast({
        title: skill.installed
          ? t('SKILLS_TOAST_UNINSTALL_FAILED') || 'Failed to uninstall skill'
          : t('SKILLS_TOAST_INSTALL_FAILED') || 'Failed to install skill',
        description:
          error instanceof Error
            ? error.message
            : t('PLEASE_TRY_AGAIN') || 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setPendingSkillAction(null);
    }
  };

  const handleDeleteSkill = async () => {
    if (!skillToDelete) {
      return;
    }

    try {
      await deleteSkill(skillToDelete.id);
      toast({
        variant: 'success',
        title: t('SKILLS_TOAST_DELETED_TITLE') || 'Skill deleted',
        description:
          t('SKILLS_TOAST_DELETED_DESCRIPTION', { name: skillToDelete.name }) ||
          `${skillToDelete.name} has been deleted successfully.`,
      });
      setSkillToDelete(null);
    } catch (error) {
      const detail = (error as { error?: { detail?: string } })?.error?.detail;
      toast({
        title: t('SKILLS_TOAST_DELETE_FAILED') || 'Failed to delete skill',
        description: detail || t('SYSTEM_SKILLS_READ_ONLY'),
        variant: 'destructive',
      });
    }
  };

  const handleDuplicateSkill = async (skill: SkillListItem) => {
    setDuplicatingSkillId(skill.id);
    try {
      await duplicateSkill(skill.id);
      toast({
        variant: 'success',
        title: t('SKILLS_TOAST_DUPLICATED_TITLE') || 'Skill duplicated',
        description:
          t('SKILLS_TOAST_DUPLICATED_DESCRIPTION', { name: skill.name }) ||
          `A copy of ${skill.name} was added to your skills.`,
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('SKILLS_TOAST_DUPLICATE_FAILED') || 'Failed to duplicate skill',
        description: getApiErrorDetail(error) ?? (t('PLEASE_TRY_AGAIN') || 'Please try again.'),
      });
    } finally {
      setDuplicatingSkillId(null);
    }
  };

  const handleDownloadSkill = async (skill: SkillListItem) => {
    setDownloadingSkillId(skill.id);
    try {
      const { blob, filename } = await downloadSkill(skill.id);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('SKILLS_TOAST_DOWNLOAD_FAILED') || 'Failed to download skill',
        description: getApiErrorDetail(error) ?? (t('PLEASE_TRY_AGAIN') || 'Please try again.'),
      });
    } finally {
      setDownloadingSkillId(null);
    }
  };

  return (
    <ConfigurationLayout>
      <div className="flex w-full min-w-0 flex-col gap-6 border-b border-grey-100 pb-6 dark:border-border">
        <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <ConfigurationHeader
            title={t('SKILLS_PAGE_TITLE') || 'Skills'}
            subtitle={
              t('SKILLS_PAGE_DESCRIPTION') ||
              'Create instructions that extend your workspace capabilities and make repeated tasks easier to reuse.'
            }
            icon={
              <Layers3
                width={20}
                height={20}
                strokeWidth={1.75}
                className="text-grey-900 dark:text-foreground"
              />
            }
            badge={
              <span className="inline-flex items-center rounded-md border border-grey-100 bg-grey-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-grey-500 dark:border-border dark:bg-white/10 dark:text-muted-foreground">
                {t('SKILLS_BETA') || 'Beta'}
              </span>
            }
          />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                className={`h-11 w-full shrink-0 gap-2 rounded-[100px] px-4 py-3 text-base font-medium leading-[1.3] shadow-none sm:w-auto`}
              >
                <Plus className="h-4 w-4" />
                {t('SKILLS_ADD_SKILL') || 'Add Skill'}
                <ChevronDown className="h-4 w-4 opacity-90" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              sideOffset={8}
              className="w-[min(300px,calc(100vw-2rem))] rounded-2xl border border-grey-100 bg-white p-2 shadow-xl dark:border-border dark:bg-card"
            >
              <DropdownMenuItem
                className={createSkillMenuItemClassName}
                onClick={() => {
                  setCreateSkillDraftMode(true);
                  setIsCreateSkillOpen(true);
                }}
              >
                <MessageSquare className={createSkillMenuIconClassName} />
                <div className="space-y-0.5">
                  <div className="text-[15px] font-medium leading-5 text-foreground">
                    {t('SKILLS_CREATE_WITH_CHAT') || 'Create with chat'}
                  </div>
                  <div className="text-sm leading-5 text-muted-foreground">
                    {t('SKILLS_CREATE_WITH_CHAT_DESCRIPTION') || 'Build a skill with Selise AI'}
                  </div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={createSkillMenuItemClassName}
                onClick={() => {
                  setCreateSkillDraftMode(false);
                  setIsCreateSkillOpen(true);
                }}
              >
                <FileText className={createSkillMenuIconClassName} />
                <div className="space-y-0.5">
                  <div className="text-[15px] font-medium leading-5 text-foreground">
                    {t('SKILLS_WRITE_INSTRUCTIONS') || 'Write skill instructions'}
                  </div>
                  <div className="text-sm leading-5 text-muted-foreground">
                    {t('SKILLS_WRITE_INSTRUCTIONS_DESCRIPTION') ||
                      'Great for skills easy to describe'}
                  </div>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                className={createSkillMenuItemClassName}
                onClick={() => setIsImportSkillOpen(true)}
              >
                <Upload className={createSkillMenuIconClassName} />
                <div className="space-y-0.5">
                  <div className="text-[15px] font-medium leading-5 text-foreground">
                    {t('SKILLS_UPLOAD') || 'Upload a skill'}
                  </div>
                  <div className="text-sm leading-5 text-muted-foreground">
                    {t('SKILLS_UPLOAD_DESCRIPTION') || 'Import a SKILL.md or .zip file'}
                  </div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
          <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
            <Search className="h-[15px] w-[15px] text-grey-400" />
          </div>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('SKILLS_SEARCH_PLACEHOLDER') || 'Search skills'}
            className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-col gap-10">
        <div className="inline-flex flex-wrap items-center gap-2">
          {tabs.map((tab) => {
            const count = tab.value === 'installed' ? installedCount : totalCount;
            const isActive = activeTab === tab.value;

            return (
              <button
                key={tab.value}
                type="button"
                onClick={() => setActiveTab(tab.value)}
                className={cn(
                  'inline-flex h-10 items-center rounded-lg border px-3 text-sm font-normal leading-[1.2] transition-colors',
                  isActive
                    ? 'border-grey-100 bg-grey-50 text-grey-900 dark:border-true-white/40 dark:bg-true-white/15 dark:text-true-white'
                    : 'border-transparent text-grey-500 hover:bg-grey-50 hover:text-grey-900 dark:text-true-white/75 dark:hover:bg-true-white/10 dark:hover:text-true-white'
                )}
              >
                {tab.label}
                <span
                  className={cn(
                    'ml-1.5 text-xs',
                    isActive
                      ? 'text-grey-500 dark:text-true-white/80'
                      : 'text-grey-500 dark:text-true-white/55'
                  )}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="flex min-w-0 flex-col gap-3">
         

          {isLoading ? (
            <SkillsListSkeleton />
          ) : isError ? (
            <Card className="rounded-lg border border-grey-100 bg-white shadow-none dark:border-border dark:bg-card">
              <div className="flex flex-col gap-3 p-6 text-sm text-muted-foreground">
                <span>{t('SKILLS_LOAD_ERROR') || 'Unable to load skills right now.'}</span>
                <div>
                  <Button variant="outline" size="sm" className="h-8 px-3" onClick={() => void refetch()}>
                    {t('SKILLS_TRY_AGAIN') || 'Try again'}
                  </Button>
                </div>
              </div>
            </Card>
          ) : filteredSkills.length > 0 ? (
            <>
              <div className={configureCardGridClassName}>
                {filteredSkills.map((skill) => (
                  <SkillCard
                    key={skill.id}
                    skill={skill}
                    onOpen={setSelectedSkillId}
                    onInstallToggle={handleInstallToggle}
                    isPending={pendingSkillAction?.skillId === skill.id}
                    onDeleteClick={setSkillToDelete}
                    onDuplicateClick={(s) => void handleDuplicateSkill(s)}
                    onDownloadClick={(s) => void handleDownloadSkill(s)}
                    isDuplicating={duplicatingSkillId === skill.id}
                    isDownloading={downloadingSkillId === skill.id}
                  />
                ))}
              </div>

              {totalCount > 0 ? (
                <div className="mt-6 flex w-full items-center justify-end gap-4">
                  <p className="text-sm font-normal leading-[1.2] text-grey-900 dark:text-foreground">
                    {t('PAGE_OF', {
                      page,
                      total: pageCount,
                      defaultValue: `Page ${page} of ${pageCount}`,
                    })}
                  </p>

                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={skillsPaginationButtonClassName}
                      onClick={() => setPage(1)}
                      disabled={page <= 1}
                      aria-label={t('GO_TO_FIRST_PAGE') || 'Go to first page'}
                    >
                      <FastArrowLeft width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={skillsPaginationButtonClassName}
                      onClick={() => setPage((current) => Math.max(1, current - 1))}
                      disabled={page <= 1}
                      aria-label={t('GO_TO_PREVIOUS_PAGE') || 'Go to previous page'}
                    >
                      <NavArrowLeft width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={skillsPaginationButtonClassName}
                      onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
                      disabled={page >= pageCount}
                      aria-label={t('GO_TO_NEXT_PAGE') || 'Go to next page'}
                    >
                      <NavArrowRight width={16} height={16} strokeWidth={1.75} />
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className={skillsPaginationButtonClassName}
                      onClick={() => setPage(pageCount)}
                      disabled={page >= pageCount}
                      aria-label={t('GO_TO_LAST_PAGE') || 'Go to last page'}
                    >
                      <FastArrowRight width={16} height={16} strokeWidth={1.75} />
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          ) : !isTotalCountLoading ? (
            <Card className="flex h-[315px] w-full flex-col items-center justify-center gap-6 rounded-lg border border-grey-100 bg-white p-6 shadow-none dark:border-border dark:bg-card">
              <div className="flex flex-col items-center gap-1.5 text-center">
                <Sparkles className="h-5 w-5 text-grey-500 dark:text-muted-foreground" />
                <p className="text-sm font-semibold leading-[1.2] text-grey-900 dark:text-foreground">
                  {trimmedSearch
                    ? t('SKILLS_NO_SEARCH_RESULTS') || 'No skills matched your search.'
                    : activeTab === 'installed'
                      ? t('SKILLS_NO_INSTALLED') || 'No installed skills yet.'
                      : t('SKILLS_NO_AVAILABLE') || 'No skills available.'}
                </p>
              </div>
            </Card>
          ) : null}
        </div>
      </div>

      <SkillDetailsModal
        skillId={selectedSkillId}
        open={!!selectedSkillId}
        onClose={() => setSelectedSkillId(null)}
      />
      <CreateSkillModal
        open={isCreateSkillOpen}
        onOpenChange={(open) => {
          setIsCreateSkillOpen(open);
          if (!open) setCreateSkillDraftMode(false);
        }}
        startInDraftMode={createSkillDraftMode}
      />
      <ImportSkillModal open={isImportSkillOpen} onOpenChange={setIsImportSkillOpen} />
      <ConfirmationModal
        open={!!skillToDelete}
        onOpenChange={(open) => {
          if (!open) {
            setSkillToDelete(null);
          }
        }}
        title={t('SKILLS_DELETE_TITLE') || 'Delete skill'}
        description={
          t('SKILLS_DELETE_DESCRIPTION', { name: skillToDeleteName }) ||
          `Are you sure you want to delete ${skillToDeleteName}? This action cannot be undone.`
        }
        onConfirm={handleDeleteSkill}
        confirmText={t('DELETE') || 'Delete'}
        cancelText={t('CANCEL') || 'Cancel'}
        preventAutoClose
        isLoading={isDeletingSkill}
        confirmVariant="delete"
      />
    </ConfigurationLayout>
  );
}
